import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clearSelectedMerchantId, DEFAULT_STORE_ID, getSelectedMerchantId, setSelectedMerchantId } from '@/lib/routes';

export const BRAND_COLORS = {
  brand: { hex: '#0E7A62', name: 'FlyBack Green', start: '#0E7A62', end: '#0A654F' },
  // Backward-compatibility alias for already-saved merchants using "black".
  black: { hex: '#0E7A62', name: 'FlyBack Green', start: '#0E7A62', end: '#0A654F' },
  red: { hex: '#e23138', name: 'Red', start: '#f3585c', end: '#e23138' },
  orange: { hex: '#f59e0b', name: 'Amber', start: '#fbbf24', end: '#f59e0b' },
  green: { hex: '#10b981', name: 'Emerald', start: '#34d399', end: '#10b981' },
  blue: { hex: '#3b82f6', name: 'Blue', start: '#60a5fa', end: '#3b82f6' },
  purple: { hex: '#8b5cf6', name: 'Violet', start: '#a78bfa', end: '#8b5cf6' },
  teal: { hex: '#14b8a6', name: 'Teal', start: '#2dd4bf', end: '#14b8a6' },
  pink: { hex: '#ec4899', name: 'Pink', start: '#f472b6', end: '#ec4899' },
};

function normalizeBrandColorKey(value) {
  if (!value) return 'brand';
  if (String(value).toLowerCase() === '#a5f2f3') return 'brand';
  if (String(value).toLowerCase() === '#5f9ea0') return 'brand';
  return value === 'black' ? 'brand' : value;
}

const useStore = create(
  persist(
    (set, get) => ({
      // Guest state
      guestToken: null,
      guestUser: null,
      guestTheme: 'light',
      currentMerchantId: getSelectedMerchantId() || DEFAULT_STORE_ID,
      storeInfo: null,
      merchantInfo: null,
      wallet: null,
      transactions: [],
      showEarnModal: false,
      earnModalData: null,
      showRedeemModal: false,
      redeemModalData: null,
      lastViewedTxId: null,

      // Merchant state
      merchantToken: null,
      merchantUser: null,
      merchantBrandColor: 'brand',
      merchantAiMessagesByMerchant: {},

      // Guest auth
      guestLogin: (token, user, merchantInfo) => {
        // Ensure we persist the context from the login result
        const loginMerchantId = merchantInfo?.id || user?.merchant_id || getSelectedMerchantId() || DEFAULT_STORE_ID;
        set({
          guestToken: token,
          guestUser: user,
          merchantInfo,
          storeInfo: merchantInfo || get().storeInfo,
          currentMerchantId: loginMerchantId,
        });
        // Save as last selected for future sessions
        setSelectedMerchantId(loginMerchantId);
      },
      guestLogout: () => {
        // Clear all session and context data
        set({
          guestToken: null,
          guestUser: null,
          wallet: null,
          transactions: [],
          merchantInfo: null,
          storeInfo: null,
          currentMerchantId: DEFAULT_STORE_ID,
        });
        // CRITICAL: Clear local storage context on logout
        window.localStorage.removeItem('flyback_entry_merchant_id');
        window.localStorage.removeItem('entry_merchant_id');
        window.localStorage.removeItem('flyback_last_selected_restaurant');
        clearSelectedMerchantId();
        // Clear any pending guest auth context used by selection flow
        window.sessionStorage.removeItem('flyback_pending_guest_phone');
        window.sessionStorage.removeItem('flyback_pending_guest_otp');
      },
      setGuestUser: (guestUser) => set({ guestUser }),
      setCurrentMerchantId: (merchantId) => {
        const value = String(merchantId || '').trim() || DEFAULT_STORE_ID;
        setSelectedMerchantId(value);
        set({ currentMerchantId: value });
      },
      setStoreInfo: (storeInfo) => set({ storeInfo, merchantInfo: storeInfo || get().merchantInfo }),
      setMerchantInfo: (merchantInfo) => set({ merchantInfo, storeInfo: merchantInfo || get().storeInfo }),
      setWallet: (wallet) => set({ wallet }),
      setTransactions: (transactions) => set({ transactions }),
      toggleGuestTheme: () => {
        const t = get().guestTheme === 'light' ? 'dark' : 'light';
        set({ guestTheme: t });
      },

      // Merchant auth
      merchantLogin: (token, user) => {
        set({
          merchantToken: token,
          merchantUser: user,
          merchantBrandColor: normalizeBrandColorKey(user?.brand_color),
        });
      },
      merchantLogout: () => {
        set({ merchantToken: null, merchantUser: null, merchantAiMessagesByMerchant: {} });
      },
      setMerchantUser: (user) => set({ merchantUser: user }),
      setMerchantBrandColor: (color) => set({ merchantBrandColor: normalizeBrandColorKey(color) }),
      setMerchantAiMessages: (merchantId, messages) =>
        set({
          merchantAiMessagesByMerchant: {
            ...get().merchantAiMessagesByMerchant,
            [merchantId || 'default']: messages,
          },
        }),
      clearMerchantAiMessages: (merchantId) => {
        const key = merchantId || 'default';
        const next = { ...get().merchantAiMessagesByMerchant };
        delete next[key];
        set({ merchantAiMessagesByMerchant: next });
      },
      setLastViewedTxId: (lastViewedTxId) => set({ lastViewedTxId }),

      // Modals
      openEarnModal: (data) => set({ showEarnModal: true, earnModalData: data }),
      closeEarnModal: () => set({ showEarnModal: false, earnModalData: null }),
      openRedeemModal: (data) => set({ showRedeemModal: true, redeemModalData: data }),
      closeRedeemModal: () => set({ showRedeemModal: false, redeemModalData: null }),

      // Sidebar
      sidebarCollapsed: false,
      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
    }),
    {
      name: 'flyback-storage',
      partialize: (state) => ({
        guestToken: state.guestToken,
        guestUser: state.guestUser,
        guestTheme: state.guestTheme,
        storeInfo: state.storeInfo,
        merchantInfo: state.merchantInfo,
        lastViewedTxId: state.lastViewedTxId,
        merchantToken: state.merchantToken,
        merchantUser: state.merchantUser,
        merchantBrandColor: state.merchantBrandColor,
        merchantAiMessagesByMerchant: state.merchantAiMessagesByMerchant,
      }),
    }
  )
);

export default useStore;
